import React, { createContext, useContext, useState, useCallback } from 'react';

import { getCurrentUser, login as loginService, removeCurrentUser } from '~services/AuthServices';

interface AuthContextValue {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  login: async () => false,
  logout: () => undefined
});

export const AuthProvider: React.FC = ({ children }) => {
  // Hydrate synchronously on first render: re-attaches the Bearer header and
  // validates client-side expiry. Reverts to anonymous (false) if expired/missing.
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => getCurrentUser());

  const login = useCallback(async (username: string, password: string) => {
    const result = await loginService(username, password);
    setIsAuthenticated(result.ok);

    return result.ok;
  }, []);

  const logout = useCallback(() => {
    removeCurrentUser();
    setIsAuthenticated(false);
  }, []);

  return <AuthContext.Provider value={{ isAuthenticated, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => useContext(AuthContext);

export default AuthContext;
